import { sanitizeForUrl } from '../routing/generate-url';
import { stripCompanyLegalSuffixForTitle } from '../seo/build-job-listing-meta-title';

/**
 * Employer profile URL slugs: `/fi/tyonantaja/terveystalo`, not `/fi/yritys/Terveystalo%20Oy`.
 *
 * Searchers type the brand ("terveystalo työpaikat"), never the legal form, so the slug is the
 * brand: legal suffix stripped (`stripCompanyLegalSuffixForTitle`), ASCII-folded, hyphenated,
 * lowercase. Company identity stays the raw `companyId`; the slug is derived data carried on the
 * company search record (`AlgoliaCompanyRecord.profileSlug`, Typesense `companies.slug`) and made
 * sticky through the `company_score` row so a company's URL does not change between nightly runs.
 *
 * Collisions are real but rare (2026-09-04: 142 groups out of ~89.7k indexable companies, nearly
 * all duplicate records of one employer such as "Attendo Oy" and a jobless "Attendo"). They are
 * resolved once per scoring run by `assignCompanyProfileSlugs`: the record with the most live jobs
 * keeps the brand slug, the rest fall back to their full-name slug, then a numeric suffix.
 */

const MIN_SLUG_LENGTH = 3;

/** True when `value` is already in the canonical slug form this module produces. */
export function isCompanyProfileSlug(value: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}

/** Full-name slug, no suffix stripping: "Wulff Works Oy" -> "wulff-works-oy". */
export function buildCompanyFullNameSlug(name: string): string {
  return sanitizeForUrl(name.trim());
}

/**
 * Brand slug: "Terveystalo Oy" -> "terveystalo", "Lassila & Tikanoja Oyj" -> "lassila-tikanoja".
 * Falls back to the full-name slug when stripping leaves too little, and to a fixed token when
 * the name has no URL-safe characters at all (never returns an empty string).
 */
export function buildCompanyProfileSlugBase(name: string): string {
  const trimmed = name.trim();
  const stripped = sanitizeForUrl(stripCompanyLegalSuffixForTitle(trimmed));
  if (stripped.length >= MIN_SLUG_LENGTH) return stripped;
  const full = buildCompanyFullNameSlug(trimmed);
  if (full) return full;
  return 'company';
}

export interface CompanyProfileSlugCandidate {
  /** Canonical companyId (the DynamoDB partition key / Typesense document id). */
  id: string;
  /** Display name; the id itself when unknown (ids are names for almost every company). */
  name?: string;
  /** Slug persisted by a previous run (company_score.profileSlug); kept when still available. */
  stickySlug?: string;
  /** Live open-job count, the tie-breaker for a contested brand slug. */
  jobCount?: number;
  /** hasSufficientContent: sitemap-worthy companies outrank thin duplicates for a contested slug. */
  sufficient?: boolean;
}

function rankOf(c: CompanyProfileSlugCandidate): [number, number, string] {
  return [c.sufficient ? 0 : 1, -(c.jobCount ?? 0), c.id];
}

function compareRank(a: CompanyProfileSlugCandidate, b: CompanyProfileSlugCandidate): number {
  const [sa, ja, ia] = rankOf(a);
  const [sb, jb, ib] = rankOf(b);
  if (sa !== sb) return sa - sb;
  if (ja !== jb) return ja - jb;
  return ia < ib ? -1 : ia > ib ? 1 : 0;
}

/**
 * Assigns one unique profile slug per company, deterministically.
 *
 * 1. Companies are ranked: sitemap-worthy first, then most live jobs, then id.
 * 2. Valid sticky slugs are honoured in rank order (a sticky slug two records both claim goes to
 *    the higher-ranked one).
 * 3. Everyone else takes, in rank order, the first free option of: brand slug, full-name slug,
 *    full-name slug with `-2`, `-3`, ...
 *
 * Deterministic for a given input, so the nightly run and the stream scorer agree as long as they
 * see the same sticky values.
 */
export function assignCompanyProfileSlugs(candidates: CompanyProfileSlugCandidate[]): Map<string, string> {
  const ranked = [...candidates].sort(compareRank);
  const claimed = new Map<string, string>(); // slug -> id
  const assigned = new Map<string, string>(); // id -> slug

  for (const c of ranked) {
    const sticky = c.stickySlug?.trim();
    if (sticky && isCompanyProfileSlug(sticky) && !claimed.has(sticky)) {
      claimed.set(sticky, c.id);
      assigned.set(c.id, sticky);
    }
  }

  for (const c of ranked) {
    if (assigned.has(c.id)) continue;
    const name = (c.name ?? c.id).trim() || c.id;
    const base = buildCompanyProfileSlugBase(name);
    const full = buildCompanyFullNameSlug(name) || base;
    const options = [base, full];
    let slug: string | undefined = options.find((s) => !claimed.has(s));
    for (let n = 2; slug === undefined; n++) {
      const candidate = `${full}-${n}`;
      if (!claimed.has(candidate)) slug = candidate;
    }
    claimed.set(slug, c.id);
    assigned.set(c.id, slug);
  }

  return assigned;
}

/**
 * Among name-search candidates, the company whose brand slug (or full-name slug) equals
 * `slug`; the one with the most recent postings wins, mirroring `assignCompanyProfileSlugs`.
 * Used when a slug-shaped URL misses the Typesense `slug` lookup: between a deploy and the
 * next scoring run the index still holds pre-slug values, and every link already uses slugs.
 */
export function pickCompanyForDerivedSlug<T extends { objectID: string; companyName?: string; recentJobsPerMonth?: number }>(
  candidates: T[],
  slug: string,
): T | null {
  const wanted = slug.trim().toLowerCase();
  if (!wanted) return null;
  const matches = candidates.filter((c) => {
    const name = (c.companyName ?? c.objectID).trim() || c.objectID;
    return buildCompanyProfileSlugBase(name) === wanted || buildCompanyFullNameSlug(name) === wanted;
  });
  if (matches.length === 0) return null;
  return [...matches].sort((a, b) => (b.recentJobsPerMonth ?? 0) - (a.recentJobsPerMonth ?? 0) || a.objectID.localeCompare(b.objectID))[0];
}
